import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { GOV_DISCLAIMER_QA } from "../../governance/govData";
import { useGov } from "../../governance/GovContext";
import { savePdf } from "../../governance/govPdf";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const DEFAULTS = { interactions: 50000, reviewers: 4, pctReviewed: 3, minutes: 12, cost: 35, targetCoverage: 100, pctHuman: 5 };

function Field({ label, value, onChange, suffix }) {
  return (
    <div>
      <label className="text-sm text-foreground">{label}</label>
      <div className="mt-1.5 flex items-center gap-2">
        <Input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className="bg-navy-800 border-white/12" />
        {suffix && <span className="text-xs text-muted-foreground shrink-0">{suffix}</span>}
      </div>
    </div>
  );
}

function Bar({ label, value, max, tone }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-44 text-xs text-muted-foreground shrink-0">{label}</span>
      <div className="flex-1 h-3 rounded-full bg-white/5 overflow-hidden"><div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(100, (value / max) * 100)}%` }} /></div>
      <span className="w-24 text-right text-xs text-foreground">{value.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</span>
    </div>
  );
}

export default function QaCalculator() {
  const { qa, setQa } = useGov();
  const [v, setV] = useState(qa || DEFAULTS);
  const set = (k, val) => { const n = { ...v, [k]: val }; setV(n); setQa(n); };

  const c = useMemo(() => {
    const reviewed = Math.round(v.interactions * (v.pctReviewed / 100));
    const notReviewed = v.interactions - reviewed;
    const manualHours = (reviewed * v.minutes) / 60;
    const manualCost = manualHours * v.cost;
    const riskVolume = Math.round(v.interactions * (v.targetCoverage / 100) * (v.pctHuman / 100));
    const riskHours = (riskVolume * v.minutes) / 60;
    return { reviewed, notReviewed, manualHours, manualCost, coverage: v.pctReviewed, riskVolume, riskHours, hoursDiff: manualHours - riskHours };
  }, [v]);

  const maxVol = Math.max(c.reviewed, c.notReviewed, c.riskVolume, 1);
  const maxHours = Math.max(c.manualHours, c.riskHours, 1);

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6 space-y-4">
        <p className="text-sm font-semibold text-foreground">Your current process</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Monthly interactions" value={v.interactions} onChange={(x) => set("interactions", x)} />
          <Field label="QA reviewers" value={v.reviewers} onChange={(x) => set("reviewers", x)} />
          <Field label="Currently reviewed" value={v.pctReviewed} onChange={(x) => set("pctReviewed", x)} suffix="%" />
          <Field label="Minutes per review" value={v.minutes} onChange={(x) => set("minutes", x)} suffix="min" />
          <Field label="Reviewer cost" value={v.cost} onChange={(x) => set("cost", x)} suffix="£/hr" />
        </div>
        <p className="text-sm font-semibold text-foreground pt-2">Illustrative risk-based model</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Automated screening coverage" value={v.targetCoverage} onChange={(x) => set("targetCoverage", x)} suffix="%" />
          <Field label="Expected human review" value={v.pctHuman} onChange={(x) => set("pctHuman", x)} suffix="% flagged" />
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Current model · sample-based manual QA</p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Stat label="Interactions reviewed" value={c.reviewed.toLocaleString("en-GB")} />
            <Stat label="Not manually reviewed" value={c.notReviewed.toLocaleString("en-GB")} />
            <Stat label="Manual review hours" value={Math.round(c.manualHours).toLocaleString("en-GB")} />
            <Stat label="Estimated manual QA cost" value={`£${Math.round(c.manualCost).toLocaleString("en-GB")}`} />
          </div>
        </div>
        <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Illustrative risk-based model · broad screening + targeted review</p>
          <div className="mt-3 space-y-2.5">
            <Bar label="Currently reviewed" value={c.reviewed} max={maxVol} tone="bg-signal-amber" />
            <Bar label="Risk-based human review" value={c.riskVolume} max={maxVol} tone="bg-primary" />
            <Bar label="Manual hours (current)" value={Math.round(c.manualHours)} max={maxHours} tone="bg-signal-amber" />
            <Bar label="Review hours (risk-based)" value={Math.round(c.riskHours)} max={maxHours} tone="bg-primary" />
          </div>
          <p className="mt-3 text-sm text-muted-foreground">Indicative reviewer-hour difference: <span className="text-foreground">{Math.round(c.hoursDiff).toLocaleString("en-GB")} hrs/month</span>. Screening coverage could expand from {c.coverage}% toward {v.targetCoverage}% with targeted human review.</p>
        </div>
        <p className="text-xs text-muted-foreground/80">{GOV_DISCLAIMER_QA}</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button asChild className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/book-a-demo">Book a norse3 walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
          <Button variant="outline" onClick={() => savePdf.qa(v)} data-testid="qa-download-pdf" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Download className="mr-1.5 h-4 w-4" /> Download PDF</Button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3"><p className="font-heading text-xl font-bold text-foreground">{value}</p><p className="text-[0.7rem] text-muted-foreground mt-0.5">{label}</p></div>;
}
